// @ts-nocheck
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ReactionStat {
  reaction_type: string;
  count: number;
  emoji: string;
  label: string;
}

export function useUserReactionStats(profileId: string | undefined) {
  return useQuery({
    queryKey: ['user-reaction-stats', profileId],
    queryFn: async () => {
      if (!profileId) throw new Error('No profile ID');

      // Get all posts by this user
      const { data: posts, error: postsError } = await supabase
        .from('wall_posts')
        .select('id')
        .eq('profile_id', profileId)
        .eq('is_hidden', false);

      if (postsError) throw postsError;
      if (!posts || posts.length === 0) return [];

      const postIds = posts.map(p => p.id);

      // Get all reactions on these posts
      const { data: reactions, error: reactionsError } = await supabase
        .from('wall_post_reactions')
        .select('reaction_type')
        .in('post_id', postIds);

      if (reactionsError) throw reactionsError;

      // Get reaction types for emoji/label mapping
      const { data: reactionTypes, error: typesError } = await supabase
        .from('wall_reaction_types')
        .select('id, emoji, label')
        .eq('is_active', true);

      if (typesError) throw typesError;

      // Create a map for quick lookup
      const typeMap = new Map(
        (reactionTypes || []).map(rt => [rt.id, { emoji: rt.emoji, label: rt.label }])
      );

      // Aggregate reactions by type
      const countMap = new Map<string, number>();
      (reactions || []).forEach(r => {
        const current = countMap.get(r.reaction_type) || 0;
        countMap.set(r.reaction_type, current + 1);
      });

      // Convert to array with emoji info
      const stats: ReactionStat[] = Array.from(countMap.entries())
        .map(([reaction_type, count]) => {
          const typeInfo = typeMap.get(reaction_type);
          return {
            reaction_type,
            count,
            emoji: typeInfo?.emoji || '👍',
            label: typeInfo?.label || 'Like',
          };
        })
        .sort((a, b) => b.count - a.count);

      return stats;
    },
    enabled: !!profileId,
  });
}
